import { useCallback, useEffect, useRef, useState } from 'react';
import { isEqual, isFunction } from 'lodash';
import { preferenceKeyForUser } from 'common/constants/userPreferences';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import type { IndexedDBStorageReadResult } from 'services/storage/indexedDBStorageManager.types';
import {
  getPreferenceResult,
  migrateLegacyMultiUserPreference,
  setPreference,
  subscribePreferenceChanges,
  subscribePreferenceRecovery,
} from 'services/storage/preferencesIndexedDB';
import { Logger } from 'utilities';

interface PreferenceState<T> {
  isReady: boolean;
  storageKey?: string;
  value: T;
}

const preferenceMemoryCache = new Map<string, unknown>();

const getCachedPreference = <T>(storageKey?: string): T | undefined => {
  if (!storageKey || !preferenceMemoryCache.has(storageKey)) {
    return undefined;
  }
  return preferenceMemoryCache.get(storageKey) as T;
};

const useLocalStorageByUser = <T>(
  key: string,
  initialValue: T
): readonly [T, (value: T | ((previousValue: T) => T)) => void, boolean] => {
  const [{ user }] = useStore() as unknown as UseStoreValues;
  const userId = user?.id as number | undefined;
  const storageKey = userId !== undefined ? preferenceKeyForUser(key, userId) : undefined;

  // Capture the initial value once so inline [] / {} literals don't invalidate the effect.
  const initialValueRef = useRef(initialValue);
  const cachedInitialValue = getCachedPreference<T>(storageKey);

  const [preferenceState, setPreferenceState] = useState<PreferenceState<T>>({
    isReady: cachedInitialValue !== undefined,
    storageKey: cachedInitialValue !== undefined ? storageKey : undefined,
    value: cachedInitialValue ?? initialValue,
  });
  const isCurrentUserPreference = preferenceState.storageKey === storageKey;
  const cachedValueByUser = getCachedPreference<T>(storageKey);
  const valueByUser = isCurrentUserPreference ? preferenceState.value : (cachedValueByUser ?? initialValueRef.current);
  const isReady = isCurrentUserPreference ? preferenceState.isReady : cachedValueByUser !== undefined;
  const valueByUserRef = useRef(valueByUser);
  const isReadyRef = useRef(isReady);
  const valueRevisionRef = useRef(0);
  valueByUserRef.current = valueByUser;
  isReadyRef.current = isReady;

  // Hydrate the active user's preference while the shared manager owns any recovery retries.
  useEffect(() => {
    const iv = initialValueRef.current;

    if (userId === undefined || storageKey === undefined) {
      setPreferenceState({ isReady: false, value: iv });
      return;
    }

    let cancelled = false;
    const hydrationRevision = valueRevisionRef.current;
    const cachedValue = getCachedPreference<T>(storageKey);
    setPreferenceState({
      isReady: cachedValue !== undefined,
      storageKey,
      value: cachedValue ?? iv,
    });

    const applyStorageResult = (result: IndexedDBStorageReadResult<T>) => {
      if (cancelled || valueRevisionRef.current !== hydrationRevision) {
        return;
      }

      if (result.status === 'unavailable') {
        // Keep the cached/default value usable while the storage manager owns recovery.
        setPreferenceState(previousState => ({ ...previousState, isReady: true, storageKey }));
        return;
      }

      const nextValue = result.status === 'found' ? result.value : iv;
      if (result.status === 'found') {
        preferenceMemoryCache.set(storageKey, nextValue);
      } else {
        preferenceMemoryCache.delete(storageKey);
      }

      setPreferenceState({ isReady: true, storageKey, value: nextValue });
    };

    const unsubscribeRecovery = subscribePreferenceRecovery<T>(storageKey, result => {
      applyStorageResult(result);
    });

    const load = async () => {
      try {
        let migrated: unknown;
        try {
          migrated = await migrateLegacyMultiUserPreference(key, storageKey, userId);
        } catch (error) {
          Logger.debug(`Preference hydration migration failed for key "${key}":`, error);
        }
        const result =
          migrated === undefined
            ? await getPreferenceResult<T>(storageKey)
            : { status: 'found' as const, value: migrated as T };

        applyStorageResult(result);
      } catch (error) {
        Logger.debug(`Preference hydration failed for key "${key}":`, error);
        applyStorageResult({ error, status: 'unavailable' });
      }
    };

    load();

    return () => {
      cancelled = true;
      unsubscribeRecovery();
    };
  }, [key, storageKey, userId]); // initialValue intentionally excluded — captured via ref

  // Keep the per-user in-memory value synchronized across tabs and same-tab consumers.
  useEffect(() => {
    if (!storageKey) return undefined;
    return subscribePreferenceChanges(({ key: changedKey, value }) => {
      if (changedKey !== storageKey) {
        return;
      }
      const nextValue = value === undefined ? initialValueRef.current : (value as T);
      if (isReadyRef.current && isEqual(valueByUserRef.current, nextValue)) {
        return;
      }
      valueRevisionRef.current += 1;
      valueByUserRef.current = nextValue;
      if (value === undefined) {
        preferenceMemoryCache.delete(storageKey);
      } else {
        preferenceMemoryCache.set(storageKey, nextValue);
      }
      setPreferenceState({ isReady: true, storageKey, value: nextValue });
    });
  }, [storageKey]);

  const setValue = useCallback(
    (value: T | ((previousValue: T) => T)) => {
      if (userId === undefined || storageKey === undefined) {
        return;
      }
      const nextValue = isFunction(value) ? value(valueByUserRef.current) : value;
      valueRevisionRef.current += 1;
      valueByUserRef.current = nextValue;
      preferenceMemoryCache.set(storageKey, nextValue);
      setPreferenceState({ isReady: true, storageKey, value: nextValue });
      setPreference(storageKey, nextValue).catch(() => {
        // The preference storage layer owns fallback and diagnostic logging.
      });
    },
    [storageKey, userId]
  );

  return [valueByUser, setValue, isReady] as const;
};

export default useLocalStorageByUser;
